import { supabase } from '../lib/supabase';
import { useAuth } from './useAuth';
import { useTranslation } from 'react-i18next';
import { useProfileStore } from '../stores/profile.store';

export interface UserProfile {
	id: string;
	email: string;
	name: string;
	phone?: string;
	address?: string;
	linkedin?: string;
	website?: string;
	created_at: string;
	updated_at: string;
	role: string;
	stripe_customer_id?: string;
	current_cv?: string;
	logs?: string[];
}

export const useProfile = () => {
	const { user } = useAuth();
	const { t } = useTranslation();
	const { profile, setProfile, loading, fetchProfile } = useProfileStore();

	const updateProfile = async (updates: Partial<UserProfile>) => {
		if (!user?.id) return { error: t('errors.not_authenticated') };
		try {
			const { data, error } = await supabase
				.from('profiles')
				.update({
					...updates,
					updated_at: new Date().toISOString(),
				})
				.eq('id', user.id)
				.select()
				.single();
			if (error) throw error;
			setProfile(data as UserProfile);
			return { data, error: null };
		} catch (error: any) {
			console.error('Error updating profile:', error);
			return { data: null, error };
		}
	};

	const addLogsToProfile = async (newLog: string) => {
		if (!user?.id) return;

		try {
			// 1. Get current logs from the store first (if available) or fetch from DB
			let currentLogs = profile?.logs || [];
			// If we don't have the profile in store, fetch it first
			if (!profile) {
				const { data: profileData, error: fetchError } = await supabase
					.from('profiles')
					.select('logs')
					.eq('id', user.id)
					.single();

				if (fetchError) throw fetchError;
				currentLogs = profileData.logs || [];
			}

			// 2. Append new log to array
			const updatedLogs = [...currentLogs, newLog];

			// 3. Update logs column in database
			const { data, error } = await supabase
				.from('profiles')
				.update({
					logs: updatedLogs,
					updated_at: new Date().toISOString(),
				})
				.eq('id', user.id)
				.select()
				.single();

			if (error) throw error;

			// 4. Update the store with the complete updated profile
			setProfile(data as UserProfile);

			return { data, error: null };
		} catch (err: any) {
			console.error('Error adding logs to profile:', err);
			return { data: null, error: err };
		}
	};

	const removeLogs = async (callback?: () => void) => {
		if (!user?.id) return;

		try {
			const { data, error } = await supabase
				.from('profiles')
				.update({
					logs: [],
					updated_at: new Date().toISOString(),
				})
				.eq('id', user.id)
				.select()
				.single();

			if (error) throw error;

			// Update the store with the updated profile
			setProfile(data as UserProfile);

			callback && callback();

			return { data, error: null };
		} catch (err: any) {
			console.error('Error removing logs from profile:', err);
			return { data: null, error: err };
		}
	};

	const updateCurrentCv = async (cvId: string | null) => {
		if (!user?.id) return { error: t('errors.not_authenticated') };
		return updateProfile({ current_cv: cvId ?? undefined });
	};

	return {
		profile,
		loading,
		updateProfile,
		addLogsToProfile,
		removeLogs,
		updateCurrentCv,
		fetchProfile: () => {
			if (user?.id) {
				return fetchProfile(user.id);
			}
			return Promise.resolve();
		},
	};
};